/**
 * Turns rendered template HTML into the `srcdoc` of the preview iframe: strips scripts,
 * embeds, inline handlers and `javascript:` URLs, adds a restrictive Content-Security-Policy,
 * and replaces link navigation with a `wb-preview-link-clicked` message to the parent.
 * Fragment previews are clamped to a card-sized column.
 *
 * @param {string} html
 * @param {{fragment?: boolean}} [options] `fragment` for `INDEX_FRAGMENT` templates
 * @returns {string}
 */
export function toPreviewDocument(html, {fragment = false} = {}) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html || '', 'text/html');

    doc.querySelectorAll('script, iframe, object, embed').forEach((node) => node.remove());
    doc.querySelectorAll('*').forEach((node) => {
        [...node.attributes].forEach((attribute) => {
            const name = attribute.name.toLowerCase();
            const value = attribute.value.trim();
            if (name.startsWith('on')) {
                node.removeAttribute(attribute.name);
                return;
            }
            if ((name === 'href' || name === 'src' || name === 'xlink:href' || name === 'formaction')
                && /^javascript:/i.test(value)) {
                node.removeAttribute(attribute.name);
            }
        });
    });

    const cspMeta = doc.createElement('meta');
    cspMeta.setAttribute('http-equiv', 'Content-Security-Policy');
    cspMeta.setAttribute(
        'content',
        "default-src 'self' data: blob: https: http:; img-src 'self' data: blob: https: http:; style-src 'self' 'unsafe-inline' https: http:; font-src 'self' data: https: http:; media-src 'self' data: blob: https: http:; object-src 'none'; child-src 'none'; frame-src 'none'; script-src 'unsafe-inline'; base-uri 'none'; form-action 'none'"
    );

    const disabledLinkStyles = doc.createElement('style');
    disabledLinkStyles.textContent = `
        a[href] {
            cursor: not-allowed !important;
        }
    `;

    const disabledLinkScript = doc.createElement('script');
    disabledLinkScript.textContent = `
        document.addEventListener('click', function(event) {
            var target = event.target;
            var element = target instanceof Element
                ? target
                : target && target.parentElement instanceof Element
                    ? target.parentElement
                    : null;
            if (!element) return;
            var anchor = element.closest('a[href]');
            if (!anchor) return;
            event.preventDefault();
            event.stopPropagation();
            // '*' is required: srcdoc iframes have a null/opaque origin so
            // window.location.origin would be "null" and drop the message.
            // The receiver validates event.source === iframe.contentWindow before acting.
            window.parent.postMessage({
                type: 'wb-preview-link-clicked',
                href: anchor.getAttribute('href') || ''
            }, '*');
        }, true);
    `;

    // INDEX_FRAGMENT templates (project cards, etc.) get placed inside a homepage
    // grid cell in production. Rendering them edge-to-edge in the iframe is
    // misleading, so clamp the body content to a card-sized container so users
    // see a representative size, not a stretched hero.
    let fragmentStyles = null;
    if (fragment) {
        fragmentStyles = doc.createElement('style');
        fragmentStyles.id = 'wb-fragment-preview-frame';
        fragmentStyles.textContent = `
            body {
                background: #f3f4f6 !important;
                padding: 2rem !important;
                display: flex !important;
                justify-content: center !important;
            }
            body > * {
                max-width: 480px !important;
                width: 100% !important;
                margin: 0 auto !important;
            }
        `;
    }

    if (doc.head) {
        doc.head.prepend(cspMeta);
        doc.head.appendChild(disabledLinkStyles);
        if (fragmentStyles) doc.head.appendChild(fragmentStyles);
    } else if (doc.documentElement) {
        const head = doc.createElement('head');
        head.appendChild(cspMeta);
        head.appendChild(disabledLinkStyles);
        if (fragmentStyles) head.appendChild(fragmentStyles);
        doc.documentElement.prepend(head);
    }
    if (doc.body) {
        doc.body.appendChild(disabledLinkScript);
    } else if (doc.documentElement) {
        doc.documentElement.appendChild(disabledLinkScript);
    }

    return doc.documentElement?.outerHTML || html;
}
